import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import {pickRandomIndices} from '../common/random';
import PlayerSetup from '../common/PlayerSetup';
import PassAndPlay from '../common/PassAndPlay';
import DiscussionTimer from '../common/DiscussionTimer';
import CategoryChips from '../common/CategoryChips';
import {useAiContent} from '../common/useAiContent';
import {hasShareHash, readShareHash} from '../common/shareLink';
import ShareLinkButton from '../common/ShareLinkButton';
import JoinRound from '../common/JoinRound';
import {CUSTOM_CATEGORY, SpyLocation} from './spyLocations';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_LOCATIONS_KEY = 'spy_custom_locations';
const DISABLED_CATEGORIES_KEY = 'spy_disabled_categories';

type Stage = 'setup' | 'passing' | 'linked' | 'playing' | 'reveal';
type Mode = 'local' | 'link';

interface AiLocation {
    name: string;
    category: string;
}

interface Round {
    location: SpyLocation;
    spyId: string;
}

interface SpySharePayload {
    players: Player[];
    location: SpyLocation;
    spyId: string;
    hostId?: string;
}

const renderSpyCard = (player: Player, data: SpySharePayload) => {
    const isSpy = player.id === data.spyId;
    if (isSpy) {
        return (
            <>
                <p className="GmSpyBadge">Ты — ШПИОН</p>
                <p className="GmSpyText">
                    Ты не знаешь локацию. Слушай остальных, пытайся понять,
                    где вы находитесь, и не спалиться раньше времени.
                </p>
            </>
        );
    }
    return <p className="GmRoleLocation">{data.location.name}</p>;
};

const renderSpyFullReveal = (data: SpySharePayload) => (
    <div style={{marginTop: 16}}>
        <p>Локация: <strong>{data.location.name}</strong></p>
        <p>Шпион: <strong>{data.players.find(p => p.id === data.spyId)?.name}</strong></p>
    </div>
);

const SpyGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customLocations, setCustomLocations] = useState<SpyLocation[]>(() => loadJSON(CUSTOM_LOCATIONS_KEY, []));
    const [disabledCategories, setDisabledCategories] = useState<string[]>(() => loadJSON(DISABLED_CATEGORIES_KEY, []));
    const [newLocationName, setNewLocationName] = useState('');
    const [stage, setStage] = useState<Stage>('setup');
    const [round, setRound] = useState<Round | null>(null);
    const [mode, setMode] = useState<Mode>('local');
    const [meId, setMeId] = useState<string | null>(null);

    const sharedHashPresent = useMemo(() => hasShareHash(), []);
    const sharedRound = useMemo(() => readShareHash<SpySharePayload>(), []);

    const aiLocations = useAiContent<AiLocation>('spy-locations.json');

    const categories = useMemo(() => {
        const set = new Set(aiLocations.map(loc => loc.category));
        return Array.from(set);
    }, [aiLocations]);

    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        aiLocations.forEach(loc => {
            counts[loc.category] = (counts[loc.category] || 0) + 1;
        });
        return counts;
    }, [aiLocations]);

    const allLocations = useMemo((): SpyLocation[] => [
        ...aiLocations.map((loc): SpyLocation => ({name: loc.name, category: loc.category, ai: true})),
        ...customLocations.map((loc): SpyLocation => ({...loc, category: CUSTOM_CATEGORY})),
    ], [aiLocations, customLocations]);

    const activeLocations = useMemo(
        () => allLocations.filter(loc => loc.custom || !disabledCategories.includes(loc.category || '')),
        [allLocations, disabledCategories]
    );

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const addCustomLocation = () => {
        const name = newLocationName.trim();
        if (!name) return;
        if (allLocations.some(loc => loc.name.toLowerCase() === name.toLowerCase())) {
            setNewLocationName('');
            return;
        }
        const next = [...customLocations, {name, custom: true}];
        setCustomLocations(next);
        saveJSON(CUSTOM_LOCATIONS_KEY, next);
        setNewLocationName('');
    };

    const removeCustomLocation = (name: string) => {
        const next = customLocations.filter(loc => loc.name !== name);
        setCustomLocations(next);
        saveJSON(CUSTOM_LOCATIONS_KEY, next);
    };

    const toggleCategory = (category: string) => {
        const next = disabledCategories.includes(category)
            ? disabledCategories.filter(c => c !== category)
            : [...disabledCategories, category];
        setDisabledCategories(next);
        saveJSON(DISABLED_CATEGORIES_KEY, next);
    };

    const startRound = () => {
        if (players.length < MIN_PLAYERS || activeLocations.length === 0) return;

        const location = activeLocations[Math.floor(Math.random() * activeLocations.length)];
        const [spyIndex] = pickRandomIndices(players.length, 1);
        const spyId = players[spyIndex].id;

        setRound({location, spyId});
        setMeId(null);
        setStage(mode === 'link' ? 'linked' : 'passing');
    };

    const playAgain = () => {
        startRound();
    };

    const backToSetup = () => {
        setRound(null);
        setMeId(null);
        setStage('setup');
    };

    if (sharedHashPresent) {
        return (
            <div className="GmPage GmPage--noBar">
                <div className="GmContainer">
                    <div className="GmTopBar">
                        <Link to="/games" className="GmBack">← Игры</Link>
                        <h1 className="GmTitle" style={{margin: 0}}>Шпион</h1>
                    </div>

                    {sharedRound ? (
                        <JoinRound
                            players={sharedRound.players}
                            data={sharedRound}
                            joinKey={window.location.hash}
                            excludePlayerId={sharedRound.hostId}
                            renderCard={renderSpyCard}
                            renderFullReveal={renderSpyFullReveal}
                        />
                    ) : (
                        <div className="GmCard">
                            <p className="GmHint GmHint--warn">Ссылка повреждена или устарела.</p>
                            <div className="GmBtnRow" style={{marginTop: 12}}>
                                <Link to="/games/spy" className="GmBtn">Начать заново</Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Шпион</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Все игроки, кроме шпиона, узнают локацию. Шпион не знает её
                            и должен вычислить по разговору, не выдав себя.
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Категории локаций ({activeLocations.length} активно)</h3>

                            {categories.length === 0 && customLocations.length === 0 && (
                                <p className="GmHint GmHint--warn">
                                    Пока нет ни одной локации. Сгенерируйте пул скриптом
                                    scripts/generate-ai-content.js или добавьте свои локации ниже.
                                </p>
                            )}

                            {categories.length > 0 && (
                                <CategoryChips
                                    categories={categories}
                                    counts={categoryCounts}
                                    disabled={disabledCategories}
                                    onToggle={toggleCategory}
                                />
                            )}

                            {customLocations.length > 0 && (
                                <div className="GmList" style={{marginTop: 12}}>
                                    {customLocations.map(loc => (
                                        <div className="GmListItem" key={loc.name}>
                                            <span className="GmListItem-name">{loc.name}</span>
                                            <span className="GmCheckItem-custom">своя</span>
                                            <button className="GmIconBtn" onClick={() => removeCustomLocation(loc.name)} aria-label="Удалить">✕</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="GmInputRow" style={{marginTop: 12}}>
                                <input
                                    className="GmInput"
                                    placeholder="Своя локация"
                                    value={newLocationName}
                                    onChange={(e) => setNewLocationName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') addCustomLocation();
                                    }}
                                />
                                <button className="GmBtn" onClick={addCustomLocation}>Добавить</button>
                            </div>
                        </div>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Как играть?</h3>
                            <div className="GmBtnRow">
                                <button
                                    className={`GmBtn ${mode === 'local' ? 'GmBtn--primary' : ''}`}
                                    onClick={() => setMode('local')}
                                >
                                    На одном телефоне
                                </button>
                                <button
                                    className={`GmBtn ${mode === 'link' ? 'GmBtn--primary' : ''}`}
                                    onClick={() => setMode('link')}
                                >
                                    По ссылке
                                </button>
                            </div>
                            {mode === 'link' && (
                                <p className="GmHint" style={{marginTop: 8}}>
                                    Ты сам выберешь себя из списка игроков, а остальным нужно будет открыть
                                    ссылку и сделать то же самое.
                                </p>
                            )}
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activeLocations.length === 0}
                                    onClick={startRound}
                                >
                                    {mode === 'link' ? 'Создать игру по ссылке' : 'Начать раунд'}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'passing' && round && (
                    <PassAndPlay
                        players={players}
                        onDone={() => setStage('playing')}
                        renderCard={(player) => renderSpyCard(player, {players, location: round.location, spyId: round.spyId})}
                    />
                )}

                {stage === 'linked' && round && !meId && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Выбери, кто ты</h3>
                        <div className="GmVoteList">
                            {players.map(player => (
                                <button key={player.id} className="GmVoteOption" onClick={() => setMeId(player.id)}>
                                    {player.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {stage === 'linked' && round && meId && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Ты — {players.find(p => p.id === meId)?.name}</h3>
                        {renderSpyCard(players.find(p => p.id === meId)!, {players, location: round.location, spyId: round.spyId})}

                        <div style={{marginTop: 18}}>
                            <DiscussionTimer defaultMinutes={8}/>
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <ShareLinkButton
                                payload={{players, location: round.location, spyId: round.spyId, hostId: meId}}
                                label="Скопировать ссылку для игроков"
                            />
                        </div>

                        <div className="GmBtnRow" style={{marginTop: 12}}>
                            <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('reveal')}>
                                Раскрыть карты
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'playing' && round && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Обсуждение</h3>
                        <DiscussionTimer defaultMinutes={8}/>
                        <div style={{marginTop: 18}}>
                            <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('reveal')}>
                                Раскрыть карты
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'reveal' && round && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Результат</h3>
                        <p>Локация: <strong>{round.location.name}</strong></p>
                        <p>Шпион: <strong>{players.find(p => p.id === round.spyId)?.name}</strong></p>
                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={playAgain}>Новый раунд</button>
                            <button className="GmBtn" onClick={backToSetup}>Изменить игроков</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SpyGame;
